import React from 'react';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useCategories } from 'application/hooks/useCategories';
import { useImportWizardStore } from '../../store/useImportWizardStore';
import ColumnSelect from './ColumnSelect';
import ValueMapEditor from './ValueMapEditor';
import type { MappingConfig } from '../../utils/types';

type StageStrategy = MappingConfig['stageStrategy'];

const StageStrategyConfig: React.FC = () => {
  const headers = useImportWizardStore((s) => s.headers);
  const fileID = useImportWizardStore((s) => s.fileID);
  const categoryID = useImportWizardStore(
    (s) => s.mappingConfig.categoryID ?? ''
  );
  const stageStrategy = useImportWizardStore(
    (s) => s.mappingConfig.stageStrategy
  );
  const updateMappingConfig = useImportWizardStore(
    (s) => s.updateMappingConfig
  );

  const { categories } = useCategories();
  const selectedCategory = categories.find((cat) => cat.id === categoryID);
  const stages = (selectedCategory?.stages ?? []).filter(
    (s) => s.id && s.title
  );
  const stageOptions = stages.map((s) => ({ id: s.id!, title: s.title! }));

  const strategyType = stageStrategy?.type ?? 'single_value';

  const getColumn = (): string => {
    if (
      stageStrategy &&
      (stageStrategy.type === 'column_id' ||
        stageStrategy.type === 'column_value_match')
    ) {
      return stageStrategy.column;
    }
    return '';
  };

  const getDefaultStageID = (): string => {
    if (stageStrategy?.type === 'column_id')
      return stageStrategy.defaultStageID;
    if (stageStrategy?.type === 'column_value_match')
      return stageStrategy.defaultStageID;
    return '';
  };

  const getStageID = (): string => {
    if (stageStrategy?.type === 'single_value') return stageStrategy.stageID;
    return '';
  };

  const getValueMap = (): Record<string, string> => {
    if (stageStrategy?.type === 'column_value_match')
      return stageStrategy.valueMap;
    return {};
  };

  const handleStrategyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const type = event.target.value as StageStrategy['type'];
    let newStrategy: StageStrategy;
    switch (type) {
      case 'column_id':
        newStrategy = { type: 'column_id', column: '', defaultStageID: '' };
        break;
      case 'column_value_match':
        newStrategy = {
          type: 'column_value_match',
          column: '',
          valueMap: {},
          defaultStageID: '',
        };
        break;
      default:
        newStrategy = { type: 'single_value', stageID: '' };
    }
    updateMappingConfig({ stageStrategy: newStrategy });
  };

  const showColumnSelector =
    strategyType === 'column_id' || strategyType === 'column_value_match';
  const showDefaultStageID =
    strategyType === 'column_id' || strategyType === 'column_value_match';
  const showValueMapEditor = strategyType === 'column_value_match';

  return (
    <FormControl component="fieldset" fullWidth>
      <FormLabel component="legend" id="stage-strategy-label">
        Stage Assignment Strategy
      </FormLabel>
      <RadioGroup
        aria-labelledby="stage-strategy-label"
        name="stageStrategy"
        value={strategyType}
        onChange={handleStrategyChange}
      >
        <FormControlLabel
          value="single_value"
          control={<Radio />}
          label="Single Value"
        />
        <FormControlLabel
          value="column_id"
          control={<Radio />}
          label="Column (Stage IDs)"
        />
        <FormControlLabel
          value="column_value_match"
          control={<Radio />}
          label="Column Value Match"
        />
      </RadioGroup>

      {strategyType === 'single_value' && (
        <Box sx={{ mt: 2 }}>
          <Autocomplete
            options={stageOptions}
            getOptionLabel={(opt) => opt.title}
            value={stageOptions.find((s) => s.id === getStageID()) || null}
            onChange={(_e, val) => {
              updateMappingConfig({
                stageStrategy: { type: 'single_value', stageID: val?.id || '' },
              });
            }}
            renderInput={(params) => (
              <TextField {...params} label="Stage" size="small" />
            )}
            ListboxProps={{ style: { maxHeight: 200 } }}
            sx={{ maxWidth: 400 }}
            size="small"
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
          />
        </Box>
      )}

      {showColumnSelector && (
        <Box sx={{ mt: 2 }}>
          <ColumnSelect
            label="Stage Column"
            value={getColumn()}
            headers={headers}
            onChange={(val) => {
              if (strategyType === 'column_id') {
                updateMappingConfig({
                  stageStrategy: {
                    type: 'column_id',
                    column: val,
                    defaultStageID: getDefaultStageID(),
                  },
                });
              } else {
                updateMappingConfig({
                  stageStrategy: {
                    type: 'column_value_match',
                    column: val,
                    valueMap: {},
                    defaultStageID: getDefaultStageID(),
                  },
                });
              }
            }}
          />
        </Box>
      )}

      {showDefaultStageID && (
        <Box sx={{ mt: 2 }}>
          <Autocomplete
            options={stageOptions}
            getOptionLabel={(opt) => opt.title}
            value={
              stageOptions.find((s) => s.id === getDefaultStageID()) || null
            }
            onChange={(_e, val) => {
              const newDefault = val?.id || '';
              if (strategyType === 'column_id') {
                updateMappingConfig({
                  stageStrategy: {
                    type: 'column_id',
                    column: getColumn(),
                    defaultStageID: newDefault,
                  },
                });
              } else {
                updateMappingConfig({
                  stageStrategy: {
                    type: 'column_value_match',
                    column: getColumn(),
                    valueMap: getValueMap(),
                    defaultStageID: newDefault,
                  },
                });
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Default Stage" size="small" />
            )}
            ListboxProps={{ style: { maxHeight: 200 } }}
            sx={{ maxWidth: 400 }}
            size="small"
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
          />
        </Box>
      )}

      {showValueMapEditor && fileID && getColumn() && (
        <ValueMapEditor
          fileID={fileID}
          columnName={getColumn()}
          valueMap={getValueMap()}
          onChange={(newValueMap) => {
            updateMappingConfig({
              stageStrategy: {
                type: 'column_value_match',
                column: getColumn(),
                valueMap: newValueMap,
                defaultStageID: getDefaultStageID(),
              },
            });
          }}
          options={stageOptions}
        />
      )}
    </FormControl>
  );
};

export default StageStrategyConfig;
